import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiCopy,
  FiUserPlus,
  FiCheck,
  FiChevronLeft,
  FiChevronRight,
  FiDownload,
  FiMenu,
  FiX,
  FiTrendingUp,
  FiTrendingDown,
  FiCalendar,
  FiUsers,
  FiUserCheck,
  FiDollarSign,
  FiAward,
  FiShare2,
  FiRefreshCw
} from 'react-icons/fi';
import {
  BsGraphUp,
  BsPersonCheck,
  BsPeople,
  BsWallet2,
  BsStarFill,
  BsTrophy,
  BsGem,
  BsLightningCharge
} from 'react-icons/bs';
import { useSelector } from 'react-redux';
import { sendApiRequest } from '../utils';
import { toast } from 'react-toastify';
import { AllPartnerStages } from '../utils';

const Overview = () => {
  const [isCopied, setIsCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('account');
  const [sponserName, SetSponserName] = useState(null);
  const [CurrentMonthSelf, SetCurrentMonthSelf] = useState(0);
  const [CurrentMonthTeam, SetCurrentMonthTeam] = useState(0);
  const [PreviousMonthSelf, SetPreviousMonthSelf] = useState(0);
  const [PreviousMonthTeam, SetPreviousMonthTeam] = useState(0);
  const [previousMonthName, setPreviousMonthName] = useState('');
  const [currentNewsPage, setCurrentNewsPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(3);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [newsItems, setNewsItems] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const userData = useSelector(state => state.user_info[0]?.user);
  const [stats, setStats] = useState({
    directStats: { active: 0, inactive: 0 },
    teamStats: { active: 0, inactive: 0 },
    businessStats: { self: 0, team: 0 },
    walletStats: { mainWallet: 0, totalIncome: 0 }
  });
  const [loading, setLoading] = useState(true);

  // Stats Cards Configuration
  const statsCards = [
    {
      id: 'direct',
      title: 'Direct Team',
      icon: <BsPersonCheck className="w-6 h-6" />,
      gradient: 'from-blue-500 to-indigo-600',
      bgGradient: 'from-blue-50 to-indigo-50',
      borderColor: 'border-blue-200',
      stats: [
        { label: 'Active', value: stats?.directStats?.active || 0, color: 'text-emerald-600' },
        { label: 'Inactive', value: stats?.directStats?.inactive || 0, color: 'text-red-500' }
      ]
    },
    {
      id: 'team',
      title: 'Total Team',
      icon: <BsPeople className="w-6 h-6" />,
      gradient: 'from-orange-500 to-red-500',
      bgGradient: 'from-orange-50 to-red-50',
      borderColor: 'border-orange-200',
      stats: [
        { label: 'Active', value: stats?.teamStats?.active || 0, color: 'text-emerald-600' },
        { label: 'Inactive', value: stats?.teamStats?.inactive || 0, color: 'text-red-500' }
      ]
    },
    {
      id: 'business',
      title: 'Total Business',
      icon: <BsGraphUp className="w-6 h-6" />,
      gradient: 'from-purple-500 to-pink-500',
      bgGradient: 'from-purple-50 to-pink-50',
      borderColor: 'border-purple-200',
      stats: [
        { label: 'Self', value: `₹${(Math.floor(stats?.businessStats?.self) || 0).toLocaleString()}`, color: 'text-purple-600' },
        { label: 'Team', value: `₹${(Math.floor(stats?.businessStats?.team) || 0).toLocaleString()}`, color: 'text-purple-600' }
      ]
    },
    {
      id: 'wallet',
      title: 'Wallet',
      icon: <BsWallet2 className="w-6 h-6" />,
      gradient: 'from-emerald-500 to-teal-500',
      bgGradient: 'from-emerald-50 to-teal-50',
      borderColor: 'border-emerald-200',
      stats: [
        { label: 'Total Income', value: `₹${(Math.floor(stats?.walletStats?.mainWallet) || 0).toLocaleString()}`, color: 'text-emerald-600' },
        { label: 'Balance', value: `₹${(Math.floor(stats?.walletStats?.totalIncome) || 0).toLocaleString()}`, color: 'text-emerald-600' }
      ]
    }
  ];

  const fetchAndShowSponsorDetails = async (id) => {
    try {
      const response = await sendApiRequest(`api/admin/partners/sponserGet/${id}`, {}, "GET");
      if (response.data.success) {
        SetSponserName(response?.data?.data[0]?.fullName);
      }
    } catch (error) {
      console.error('Error fetching sponsor details:', error);
    }
  };

  useEffect(() => {
    if (userData?.sponsor) {
      fetchAndShowSponsorDetails(userData?.sponsor);
    }
  }, [userData?.sponsor]);

  const webLink = `${process.env.REACT_APP_URL_BASE}/register?sponserID=${userData?.sponsorCode}`;

  useEffect(() => {
    const fetchNews = async () => {
      const response = await sendApiRequest('api/admin/get_all_news', {}, 'GET');
      setNewsItems(response.data.data);
    };
    fetchNews();
  }, []);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(webLink);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
    toast.success('Referral link copied!');
  };

  const handleNewJoining = () => {
    window.open(webLink, '_blank', 'noopener,noreferrer');
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchStats();
    await fetchCurrentMonthStats();
    await fetchPreviousMonthStats();
    setTimeout(() => setIsRefreshing(false), 1000);
    toast.success('Dashboard refreshed!');
  };

  useEffect(() => {
    if (userData?._id) {
      fetchCurrentMonthStats();
      fetchPreviousMonthStats();
    }
  }, [userData?._id]);

  const fetchCurrentMonthStats = async () => {
    try {
      const today = new Date();
      const year = today.getFullYear();
      const month = today.getMonth();
      const firstDay = new Date(Date.UTC(year, month, 1));
      const lastDay = new Date(Date.UTC(year, month + 1, 0, 23, 59, 59, 999));
      const startDateStr = firstDay.toISOString();
      const endDateStr = lastDay.toISOString();
      const response = await sendApiRequest(
        `api/overview/business-stats?startDate=${startDateStr}&endDate=${endDateStr}`,
        {},
        'GET'
      );
      if (response.status === 200) {
        const currentStats = response.data.data;
        SetCurrentMonthTeam(currentStats?.team);
        SetCurrentMonthSelf(currentStats?.self);
      }
    } catch (error) {
      console.error('Error fetching current month stats:', error);
    }
  };

  const fetchPreviousMonthStats = async () => {
    try {
      const response = await sendApiRequest(
        'api/overview/previous-month-business',
        {},
        'GET'
      );
      if (response.status === 200) {
        const previousStats = response.data.data;
        SetPreviousMonthTeam(previousStats?.team);
        SetPreviousMonthSelf(previousStats?.self);
        setPreviousMonthName(previousStats?.month || 'Previous Month');
      }
    } catch (error) {
      console.error('Error fetching previous month stats:', error);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setWindowWidth(width);
      if (width < 640) setItemsPerPage(1);
      else if (width < 1024) setItemsPerPage(2);
      else setItemsPerPage(3);
      if (width >= 768) setIsMobileMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const totalNewsPages = Math.ceil(newsItems.length / itemsPerPage);
  const currentNewsItems = newsItems.slice(
    currentNewsPage * itemsPerPage,
    (currentNewsPage + 1) * itemsPerPage
  );

  const goToNextNews = () => setCurrentNewsPage((prev) => (prev + 1) % totalNewsPages);
  const goToPrevNews = () => setCurrentNewsPage((prev) => (prev - 1 + totalNewsPages) % totalNewsPages);

  useEffect(() => {
    const interval = setInterval(goToNextNews, 8000);
    return () => clearInterval(interval);
  }, [currentNewsPage, itemsPerPage]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await sendApiRequest('api/overview/stats', {}, 'GET');
      if (response.data?.success) {
        setStats(response.data.data || {
          directStats: { active: 0, inactive: 0 },
          teamStats: { active: 0, inactive: 0 },
          businessStats: { self: 0, team: 0 },
          walletStats: { mainWallet: 0, totalIncome: 0 }
        });
      }
    } catch (error) {
      toast.error('Failed to fetch overview statistics');
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-indigo-500 mx-auto"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <BsGem className="w-6 h-6 text-indigo-400 animate-pulse" />
            </div>
          </div>
          <p className="mt-4 text-gray-500 font-medium">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Mobile Header */}
      <div className="md:hidden sticky top-0 z-30 bg-gradient-to-r from-indigo-600 to-blue-600 text-white px-4 py-4 flex justify-between items-center shadow-lg">
        <div>
          <h1 className="text-xl font-bold">Dashboard</h1>
          <p className="text-xs text-blue-100 mt-0.5">Welcome back!</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleRefresh} className="p-2 rounded-xl bg-white/20">
            <FiRefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2 rounded-xl bg-white/20">
            {isMobileMenuOpen ? <FiX className="w-5 h-5" /> : <FiMenu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white shadow-xl border-b border-gray-100"
          >
            <div className="flex flex-col p-4 space-y-2">
              <button
                onClick={() => { setActiveTab('account'); setIsMobileMenuOpen(false); }}
                className={`px-4 py-3 rounded-xl font-medium text-left transition-all ${activeTab === 'account' ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-700'}`}
              >
                👤 Account Details
              </button>
              <button
                onClick={() => { setActiveTab('news'); setIsMobileMenuOpen(false); }}
                className={`px-4 py-3 rounded-xl font-medium text-left transition-all ${activeTab === 'news' ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-700'}`}
              >
                📰 News & Events ({newsItems.length})
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {/* Header for Desktop */}
        <div className="hidden md:flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
              Dashboard Overview
            </h1>
            <p className="text-gray-500 mt-1">Welcome back, {userData?.fullName}!</p>
          </div>
          <div className="flex items-center gap-3">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleRefresh}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all"
            >
              <FiRefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setActiveTab('account')}
              className={`px-5 py-2 rounded-xl font-medium transition-all ${activeTab === 'account' ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              Account Details
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setActiveTab('news')}
              className={`px-5 py-2 rounded-xl font-medium transition-all ${activeTab === 'news' ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              News & Events ({newsItems.length})
            </motion.button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          {statsCards.map((card, idx) => (
            <motion.div
              key={card.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              whileHover={{ y: -4 }}
              className={`bg-white rounded-2xl shadow-lg border ${card.borderColor} overflow-hidden hover:shadow-xl transition-all duration-300`}
            >
              <div className={`bg-gradient-to-r ${card.bgGradient} px-5 py-4 border-b ${card.borderColor}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`p-2 rounded-xl bg-gradient-to-r ${card.gradient} text-white shadow-md`}>
                      {card.icon}
                    </div>
                    <h3 className="font-semibold text-gray-800">{card.title}</h3>
                  </div>
                  <BsStarFill className="w-4 h-4 text-yellow-400" />
                </div>
              </div>
              <div className="p-5">
                <div className="space-y-3">
                  {card.stats.map((stat, i) => (
                    <div key={i} className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">{stat.label}</span>
                      <span className={`font-bold text-lg ${stat.color}`}>{stat.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Business Comparison Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
          {/* Current Month Business */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 border border-emerald-200 shadow-lg"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 shadow-lg">
                  <BsLightningCharge className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-800">Current Month Business</h3>
              </div>
              <span className="px-3 py-1 bg-emerald-200 text-emerald-700 rounded-full text-xs font-semibold">
                {new Date().toLocaleString('default', { month: 'long' })}
              </span>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-emerald-200">
                <span className="text-gray-600">Self Business</span>
                <span className="font-bold text-xl text-emerald-600">₹{(Math.floor(CurrentMonthSelf) || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-emerald-200">
                <span className="text-gray-600">Team Business</span>
                <span className="font-bold text-xl text-emerald-600">₹{(Math.floor(CurrentMonthTeam) || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center pt-2">
                <span className="text-gray-700 font-semibold">Total Business</span>
                <span className="font-bold text-2xl text-emerald-700">₹{(Math.floor(CurrentMonthTeam + CurrentMonthSelf) || 0).toLocaleString()}</span>
              </div>
            </div>
          </motion.div>

          {/* Previous Month Business */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 border border-amber-200 shadow-lg"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 shadow-lg">
                  <FiCalendar className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-800">{previousMonthName} Business</h3>
              </div>
              <span className="px-3 py-1 bg-amber-200 text-amber-700 rounded-full text-xs font-semibold">
                Previous Month
              </span>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-amber-200">
                <span className="text-gray-600">Self Business</span>
                <span className="font-bold text-xl text-amber-600">₹{(Math.floor(PreviousMonthSelf) || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-amber-200">
                <span className="text-gray-600">Team Business</span>
                <span className="font-bold text-xl text-amber-600">₹{(Math.floor(PreviousMonthTeam) || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center pt-2">
                <span className="text-gray-700 font-semibold">Total Business</span>
                <span className="font-bold text-2xl text-amber-700">₹{(Math.floor(PreviousMonthTeam + PreviousMonthSelf) || 0).toLocaleString()}</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Account Summary Section */}
        {activeTab === 'account' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 mb-8"
          >
            <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-indigo-700 text-white px-6 py-5">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="text-xl font-bold flex items-center gap-2">
                    <BsGem className="w-5 h-5" />
                    Account Summary
                  </h2>
                  <p className="text-indigo-100 text-sm mt-1">Your complete account details at a glance</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-4 py-2 bg-white/20 rounded-xl text-sm font-medium flex items-center gap-2 backdrop-blur-sm">
                    <BsTrophy className="text-yellow-300" />
                    Rank: {userData?.rank || 'Associate'}
                  </span>
                </div>
              </div>
            </div>

            <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <p className="text-gray-500 text-xs font-medium uppercase tracking-wide">Member ID</p>
                    <p className="font-bold text-gray-800 text-lg mt-1">{userData?.sponsorCode || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <p className="text-gray-500 text-xs font-medium uppercase tracking-wide">Registration Date</p>
                    <p className="font-bold text-gray-800 text-lg mt-1">
                      {userData?.createdAt ? new Date(userData.createdAt).toLocaleDateString('en-IN') : 'N/A'}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <p className="text-gray-500 text-xs font-medium uppercase tracking-wide">Sponsor ID</p>
                    <p className="font-bold text-gray-800 text-lg mt-1">{userData?.sponsor || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <p className="text-gray-500 text-xs font-medium uppercase tracking-wide">Mobile Number</p>
                    <p className="font-bold text-gray-800 text-lg mt-1">{userData?.phone || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <p className="text-gray-500 text-xs font-medium uppercase tracking-wide">Member Name</p>
                    <p className="font-bold text-gray-800 text-lg mt-1">{userData?.fullName || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <p className="text-gray-500 text-xs font-medium uppercase tracking-wide">Sponsor Name</p>
                    <p className="font-bold text-gray-800 text-lg mt-1">{sponserName || 'N/A'}</p>
                  </div>
                  <div className="col-span-2 bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <p className="text-gray-500 text-xs font-medium uppercase tracking-wide">Email ID</p>
                    <p className="font-bold text-gray-800 text-base mt-1 break-all">{userData?.email || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Referral Section - Full Width */}
              <div className="col-span-1 lg:col-span-2">
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-5 border border-indigo-100">
                  <div className="flex items-center gap-2 mb-3">
                    <FiShare2 className="w-5 h-5 text-indigo-600" />
                    <h3 className="font-semibold text-gray-800">Your Referral Link</h3>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        value={webLink}
                        readOnly
                        className="w-full p-3 pr-12 text-sm border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-indigo-500/20 focus:outline-none"
                      />
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleCopyLink}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-indigo-600 transition-colors"
                      >
                        {isCopied ? <FiCheck className="w-5 h-5 text-emerald-500" /> : <FiCopy className="w-5 h-5" />}
                      </motion.button>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleNewJoining}
                      className="flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-600 to-blue-600 text-white px-6 py-3 rounded-xl hover:shadow-lg transition-all duration-300 font-medium"
                    >
                      <FiUserPlus className="w-4 h-4" />
                      New Joining
                    </motion.button>
                  </div>
                  <p className="text-xs text-gray-500 mt-3">Share this link with your network to grow your team</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* News and Events Section */}
        {activeTab === 'news' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 mb-8"
          >
            <div className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white px-6 py-5">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <BsStarFill className="w-5 h-5 text-yellow-300" />
                News and Events
              </h2>
              <p className="text-indigo-100 text-sm mt-1">Stay updated with the latest announcements</p>
            </div>

            <div className="relative min-h-[300px] sm:min-h-[350px] px-4 sm:px-6 pb-16 pt-4">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentNewsPage}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.4 }}
                  className="grid gap-5"
                  style={{
                    gridTemplateColumns: windowWidth < 640 ? '1fr' : windowWidth < 1024 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                  }}
                >
                  {currentNewsItems.map((item, idx) => (
                    <motion.div
                      key={item.id || idx}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: idx * 0.1 }}
                      whileHover={{ y: -4 }}
                      className="group bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300"
                    >
                      <div className="h-2 bg-gradient-to-r from-indigo-500 to-blue-500"></div>
                      <div className="p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-indigo-100 to-blue-100 flex items-center justify-center">
                              <BsStarFill className="w-4 h-4 text-indigo-500" />
                            </div>
                            <div>
                              <span className="text-xs text-gray-400">
                                {item.date?.split('T')[0] || new Date().toLocaleDateString()}
                              </span>
                              {item.isNew && (
                                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">NEW</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <h3 className="text-lg font-bold text-gray-800 mb-2 group-hover:text-indigo-600 transition-colors line-clamp-2">
                          {item.title}
                        </h3>
                        <p className="text-gray-600 text-sm leading-relaxed line-clamp-3">
                          {item.description}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              </AnimatePresence>

              {/* Navigation Controls */}
              {totalNewsPages > 1 && (
                <div className="absolute bottom-4 left-0 right-0 flex justify-center items-center gap-4">
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={goToPrevNews}
                    className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 shadow-sm transition-all"
                  >
                    <FiChevronLeft className="w-5 h-5" />
                  </motion.button>
                  <div className="flex gap-2">
                    {Array.from({ length: totalNewsPages }).map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentNewsPage(index)}
                        className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                          currentNewsPage === index ? 'bg-indigo-600 w-6' : 'bg-gray-300 hover:bg-gray-400'
                        }`}
                      />
                    ))}
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={goToNextNews}
                    className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 shadow-sm transition-all"
                  >
                    <FiChevronRight className="w-5 h-5" />
                  </motion.button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Overview;